import React, { FunctionComponent, useState, ChangeEvent } from 'react';
import {
    Grid,
    Typography,
    List,
    ListItem,
    makeStyles,
    Button,
    IconButton,
    ListItemSecondaryAction,
    ListItemText,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { FormField } from '../../../../store/modules/application/types';
import FormFieldDisplay from '../FormFieldDisplay';
import pupa from 'pupa';

export type RepeatableFormFieldProps = {
    formField: FormField;
    value: any;
    readOnly?: boolean;
    handleInputChange: Function;
    formData?: Record<string, unknown>;
    isCustomStyle?: boolean;
};

const useStyles = makeStyles(() => ({
    emptyText: {
        color: '#666666',
        fontSize: '14px',
    },
    addBtn: {
        backgroundColor: '#eeeeee',
    },
    listText: {
        '& >*': {
            fontSize: 14,
        },
    },
    dialogAddBtn: {
        background: '#0277bd',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#0484ce',
        },
    },
    repeatableFieldLabel: {
        fontWeight: 500,
    },
    container: {
        paddingTop: '16px',
    },
}));

const RepeatableFormField: FunctionComponent<RepeatableFormFieldProps> = (props) => {
    const { formField, readOnly, value, handleInputChange } = props;

    const nestedFieldsExist = formField.fields!;
    const classes = useStyles();

    const [repeatableFieldDialog, setRepeatableFieldDialog] = useState<boolean>(false);
    const [repeatableFieldsFormData, setRepeatableFieldsFormData] = useState<Record<string, unknown>>({});
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [editIndex, setEditIndex] = useState<number>(0);

    const isButton = {
        ...(!readOnly && { button: true }),
    };

    const repeatableFieldsHandleInputChange = (dataKey: string, value: unknown) => {
        setRepeatableFieldsFormData({
            ...repeatableFieldsFormData,
            [dataKey]: value,
        });
    };

    const handleAddRepeatableField = (dataKey: string) => {
        const previousData = value || [];
        const data = [...previousData, repeatableFieldsFormData];
        handleInputChange(dataKey, data);
        setRepeatableFieldDialog(false);
        setRepeatableFieldsFormData({});
    };

    const handleDeleteRepeatableField = (event: ChangeEvent<EventTarget>, dataKey: string, index: number) => {
        event.stopPropagation();
        const data = [...value];
        data.splice(index, 1);
        handleInputChange(dataKey, data);
    };

    const handleEditRepeatableField = (dataKey: string) => {
        const data = [...value];
        data.splice(editIndex, 1, repeatableFieldsFormData);
        handleInputChange(dataKey, data);
        setRepeatableFieldDialog(false);
        setRepeatableFieldsFormData({});
        setIsEdit(false);
    };

    const handleClickEdit = (data: Record<string, unknown>, index: number) => {
        setRepeatableFieldsFormData(data);
        setEditIndex(index);
        setIsEdit(true);
        setRepeatableFieldDialog(true);
    };

    const handleCloseDialog = () => {
        setRepeatableFieldDialog(false);
        setRepeatableFieldsFormData({});
        setIsEdit(false);
    };

    const evalExpression = (data: Record<string, unknown>, expression?: string) => {
        if (!expression) {
            return undefined;
        }

        return pupa(expression, data, { transform: ({ value }) => value ?? '' });
    };

    return (
        <>
            <Grid container className={classes.container}>
                <Grid item xs={12}>
                    <Typography className={classes.repeatableFieldLabel}>{formField.label}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <List>
                        {value?.length ? (
                            value.map((data: Record<string, unknown>, index: number) => (
                                <ListItem
                                    disableGutters={readOnly}
                                    key={index.toString()}
                                    onClick={!readOnly ? () => handleClickEdit(data, index) : undefined}
                                    {...isButton}
                                >
                                    <ListItemText
                                        primary={evalExpression(data, formField.displayExpression)}
                                        secondary={evalExpression(data, formField.secondaryDisplayExpression)}
                                        className={classes.listText}
                                    />
                                    {!readOnly && (
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                onClick={(event: ChangeEvent<EventTarget>) =>
                                                    handleDeleteRepeatableField(event, formField.field, index)
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                            ))
                        ) : (
                            <ListItem disableGutters={readOnly}>
                                <Typography className={classes.emptyText}>None</Typography>
                            </ListItem>
                        )}
                    </List>
                </Grid>
                {!readOnly && (
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            className={classes.addBtn}
                            onClick={() => {
                                setRepeatableFieldDialog(true);
                            }}
                        >
                            Add
                        </Button>
                    </Grid>
                )}
            </Grid>
            <Dialog fullWidth open={repeatableFieldDialog} onClose={handleCloseDialog}>
                <DialogTitle>{formField.label}</DialogTitle>
                <DialogContent>
                    {nestedFieldsExist &&
                        formField.fields?.map((entry) => (
                            <FormFieldDisplay
                                key={entry.field}
                                formField={entry}
                                readOnly={readOnly}
                                value={repeatableFieldsFormData[entry.field]}
                                handleInputChange={repeatableFieldsHandleInputChange}
                                isCustomStyle={true}
                            />
                        ))}
                </DialogContent>
                <DialogActions style={{ padding: 24 }}>
                    <Button variant="outlined" onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.dialogAddBtn}
                        onClick={() =>
                            isEdit
                                ? handleEditRepeatableField(formField?.field)
                                : handleAddRepeatableField(formField?.field)
                        }
                    >
                        {isEdit ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RepeatableFormField;
